package com.bitterinfant.webui

import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.setValue
import kotlinx.browser.window
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.*
import org.w3c.dom.Element
import org.w3c.dom.HTMLMediaElement
import kotlin.js.Promise

/** Chain-link glyph (Feather's "link"), drawn through a CSS mask so it takes the text color. */
private val linkIconMask = """
    url("data:image/svg+xml,<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='black' stroke-width='2.5' stroke-linecap='round' stroke-linejoin='round'><path d='M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71'/><path d='M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71'/></svg>") center / contain no-repeat
""".trim().replace("<", "%3C").replace(">", "%3E")

/** The one "Link copied" toast on screen: the anchor of the clip whose button shows it, and its text. */
private var toast by mutableStateOf<Pair<String, String>?>(null)

/**
 * A small link icon that copies a deep link to clip [anchor] (on the enclosing [Tabs] panel's
 * tab) to the clipboard — see [DeepLink]. If the clip's <audio>/<video> is partway through, the
 * link starts from there (`&t=`), so pausing at the good part and copying shares the good part.
 * Confirms with a toast at the bottom of the screen (so a long title never reflows), or falls
 * back to a prompt showing the link where the clipboard isn't available.
 */
@Composable
fun CopyLinkButton(anchor: String) {
    val tab = LocalTabSlug.current
    Span(attrs = {
        style {
            property("display", "inline-flex")
            alignItems(AlignItems.Center)
            marginLeft(0.4.cssRem)
            property("flex-shrink", "0")
        }
    }) {
        Button(attrs = {
            attr("type", "button")
            attr("title", "Copy link")
            attr("aria-label", "Copy a link to this clip")
            onClick { event ->
                val media = (event.target as? Element)?.closest(".media-block")
                    ?.querySelector("audio, video") as? HTMLMediaElement
                // A clip played (or scrubbed) to the end links to its start, not its last second.
                val start = media?.takeUnless { it.currentTime >= it.duration - 1 }?.currentTime?.toInt() ?: 0
                val url = DeepLink.linkTo(tab, anchor, start)
                copyToClipboard(url) { copied ->
                    if (!copied) {
                        window.prompt("Copy this link:", url)
                        return@copyToClipboard
                    }
                    val shown = anchor to if (start > 0) "Link copied (starts at ${formatTime(start)})" else "Link copied"
                    toast = shown
                    window.setTimeout({ if (toast === shown) toast = null }, 2500)
                }
            }
            style { roundIconButton() }
        }) {
            Span(attrs = {
                style {
                    display(DisplayStyle.Block)
                    width(0.7.cssRem)
                    height(0.7.cssRem)
                    property("background-color", "currentColor")
                    property("mask", linkIconMask)
                    property("-webkit-mask", linkIconMask)
                }
            })
        }
        toast?.takeIf { it.first == anchor }?.let { (_, message) ->
            Span(attrs = {
                attr("role", "status")
                style {
                    position(Position.Fixed)
                    bottom(1.5.cssRem)
                    left(50.percent)
                    property("transform", "translateX(-50%)")
                    property("z-index", "30")
                    property("max-width", "calc(100vw - 2rem)")
                    padding(0.5.cssRem, 0.9.cssRem)
                    property("background", Theme.bgElevated)
                    property("border", "1px solid ${Theme.border}")
                    property("border-radius", "6px")
                    property("box-shadow", "0 4px 12px rgba(0,0,0,0.4)")
                    property("pointer-events", "none")
                    fontSize(0.85.cssRem)
                    property("font-weight", "normal")
                    color(Color(Theme.text))
                }
            }) { Text(message) }
        }
    }
}

/** Calls [onDone] with whether [text] made it onto the clipboard (needs HTTPS or localhost). */
private fun copyToClipboard(text: String, onDone: (Boolean) -> Unit) {
    val clipboard = window.navigator.asDynamic().clipboard
    if (clipboard == null) {
        onDone(false)
        return
    }
    (clipboard.writeText(text) as Promise<Unit>).then { onDone(true) }.catch { onDone(false) }
}
